import { motion } from "framer-motion";
import { Reveal, SectionLabel } from "./Reveal";

const fmtDate = (iso) => {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString("en-US", { day: "2-digit" }),
    month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    year: d.getFullYear(),
  };
};

export const Events = ({ events = [], loading, section }) => {
  if (!section) return null;

  const maxItems = Number(section.metadata?.max_items || 2);
  const defaultTicketTarget = section.button_url || "#join";

  const openTickets = (target, event) => {
    if (event?.enquiry) {
      window.dispatchEvent(new CustomEvent("susegado:event-enquiry", {
        detail: { eventTitle: event.title },
      }));
      return;
    }

    if (target.startsWith("#")) {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.location.href = target;
  };

  return (
    <section id="events" data-testid="events-section" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel index={section.metadata?.section_index || "01"}>{section.eyebrow}</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-anton uppercase text-[#FAFAFA] text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-16 max-w-4xl">
            {section.title} <span className="text-[#E85A24]">{section.title_accent}</span>
          </h2>
        </Reveal>

        {loading && (
          <p data-testid="events-loading" className="font-oswald uppercase tracking-widest text-[#A3A3A3]">
            {section.metadata?.loading_text || "Loading fight card…"}
          </p>
        )}

        <div className="flex flex-col">
          {events.slice(0, maxItems).map((event, index) => {
            const date = fmtDate(event.date);
            const ticketTarget = event.ticket_url || defaultTicketTarget;
            const actionLabel = event.enquiry ? "Enquire" : (section.metadata?.tickets_label || "Tickets");
            const actionClass = event.enquiry
              ? "bg-[#E85A24] text-black hover:bg-[#FF7038]"
              : "text-[#E85A24] hover:bg-[#E85A24] hover:text-black";

            return (
              <Reveal key={event.id} delay={index * 0.08}>
                <motion.article
                  data-testid={`event-card-${event.id}`}
                  className="group relative grid grid-cols-1 md:grid-cols-12 items-center gap-6 border-t border-white/10 py-8 md:py-10 last:border-b overflow-hidden"
                >
                  <div className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <img src={event.image} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/75" />
                  </div>

                  <div className="relative z-10 md:col-span-2 flex md:flex-col items-baseline md:items-start gap-3 md:gap-0">
                    <span className="font-anton text-5xl md:text-6xl text-[#E85A24] leading-none">{date.day}</span>
                    <span className="font-oswald uppercase tracking-[0.25em] text-sm text-[#FAFAFA]">
                      {date.month} {date.year}
                    </span>
                  </div>

                  <div className="relative z-10 md:col-span-6">
                    <h3 className="font-anton uppercase text-2xl md:text-4xl text-[#FAFAFA] leading-none group-hover:text-[#E85A24] transition-colors duration-300">
                      {event.title}
                    </h3>
                    <p className="font-oswald uppercase tracking-widest text-xs md:text-sm text-[#A3A3A3] mt-2">
                      Main Event — {event.main_event}
                    </p>
                  </div>

                  <div className="relative z-10 md:col-span-3">
                    <p className="font-dm text-[#FAFAFA]">{event.venue}</p>
                    <p className="font-dm text-sm text-[#A3A3A3]">{event.city}</p>
                  </div>

                  <div className="relative z-10 md:col-span-1 flex md:justify-end">
                    <button
                      data-testid={event.enquiry ? `event-enquire-${event.id}` : `event-tickets-${event.id}`}
                      onClick={() => openTickets(ticketTarget, event)}
                      className={`font-oswald uppercase tracking-[0.2em] text-xs border border-[#E85A24] px-5 py-3 transition-colors duration-300 whitespace-nowrap ${actionClass}`}
                    >
                      {actionLabel}
                    </button>
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
